/**
 * *********************************************************************************************************************
 * Responsibility: Tt will convert a string input into an array of separate inputs separated by the prop "separator."
 * It has very basic email validation and will not convert the input into a tag unless it is a valid email.
 * Since the hook makes use of the native onkeydown event onChange events, it will not propagate the events
 * further up the tree unless passed in as a prop.
 * *********************************
 *
 * @format
 */

import React, {useEffect, useRef, useState} from 'react';

export const useEmailList = ({value, separator, onKeyDown, onChange}) => {
    const [isValidEmail, setIsValidEmail] = useState(true);
    const initialValue = !value ? [] : value.split(separator);
    const [inputValue, setInputValue] = useState('');
    const [items, setItems] = useState(initialValue);
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [items]);

    useEffect(() => {
        const initialValue = !value ? [] : value.replaceAll(',', ' ').split(separator);
        setItems(initialValue);
    }, [value]);

    // make sure that the entry looks like an email
    const validateEmail = email => {
        const re = /\S+@\S+\.\S+/;
        return re.test(email);
    };

    // check the input and perform the corresponding actions
    function handleChange() {
        const value = inputRef.current.value;

        if (!value) {
            setInputValue('');
            return;
        }

        const isValidEmail = validateEmail(value);
        setIsValidEmail(isValidEmail);

        // don't convert anything to tags unless is a valid email
        if (!isValidEmail) {
            setInputValue(value);
            return;
        }

        if (value.endsWith(separator)) {
            const newItem = value.replaceAll(' ', '');
            const newItems = items;
            if (newItem) {
                newItems.push(newItem);
                setItems(newItems);
            }
            setInputValue('');
            setIsValidEmail(true);
            onChange(cleanString(newItems, ''));
        } else {
            setInputValue(value);
        }
    }

    // remove any unwanted characters from the string
    function cleanString(items = [], value = '') {
        if (items.length < 1) {
            return '';
        }

        let itemsString = items.join(',') + (value ? `,${value}` : '');
        if (itemsString.endsWith(separator)) {
            itemsString = itemsString.slice(0, -1);
        }
        if (itemsString.startsWith(separator)) {
            itemsString = itemsString.slice(1);
        }
        return itemsString;
    }

    // clear whatever is on the input to prevent it from being added to the list
    const handleClearInput = () => {
        if (inputValue) {
            setIsValidEmail(true);
            setInputValue('');
            onChange(cleanString(items, ''));
        }
    };

    const handleBlur = e => {
        const value = e.target.value;

        if (validateEmail(value)) {
            e.target.value = e.target.value + separator;
            handleChange(e);
        } else {
            handleClearInput();
        }
    };

    // delete an item from the array
    const handleDelete = index => {
        items.splice(index, 1);
        setItems(items);

        onChange(cleanString(items, ''));
    };

    // check if the user is deleting entry
    function handleKeyDown(e) {
        onKeyDown && onKeyDown(e);
        if (e.key === 'Backspace' && !inputValue && items.length > 0) {
            const lastItem = items[items.length - 1];
            setItems(items.slice(0, -1));
            setInputValue(lastItem);
        } else if (e.key === 'Enter') {
            inputRef.current.value = inputRef.current.value + separator;
            handleChange();
        }
    }

    return {
        handleClearInput,
        handleKeyDown,
        handleDelete,
        handleChange,
        isValidEmail,
        inputValue,
        handleBlur,
        inputRef,
        items
    };
};
